import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import useProductGroupOptions from 'Services/ProductManagement/Features/Parameter/Hooks/useProductGroupOptions'
import { useGetCustomerGroupParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import ProductDrawer from 'Services/ProductManagement/Features/Product/Components/ProductDrawer'
import { ProductList } from 'Services/ProductManagement/Features/Product/Components/ProductList'
import {
  useProductsWithParams,
  useSingleProduct
} from 'Services/ProductManagement/Features/Product/Hooks'
import useProductListTabs from 'Services/ProductManagement/Features/Product/Hooks/useProductListTabs'
import { UnsavedChangesProvider } from 'Util/Context/unsavedChangesContext'
import { PRODUCT_MANAGEMENT_ROOT_URL, ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Tab } from '@mui/material'
import Box from '@mui/material/Box'
import { unwrapResult } from '@reduxjs/toolkit'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

function ProductListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/products`
  const navigate = useNavigate()

  const {
    products,
    loading,
    searchQueryParams,
    setSearchQueryParams,
    total_results,
    selectedProductId,
    setSelectedProductId,
    fetchProductsWithParams
  } = useProductsWithParams({
    hasNoTariff: true
  })

  const { deleteProduct } = useSingleProduct(undefined)

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const [isSelectedListItemDrawerOpen, setIsSelectedListItemDrawerOpen] = useState(
    !!selectedProductId
  )

  const { tabFromParams, handleTabChange } = useProductListTabs({
    setSearchQueryParams
  })

  const {
    data: customerGroupParameterGroup = null,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery()

  const { productGroupOptions, isProductGroupParameterGroupLoading } = useProductGroupOptions()

  const handleSelectedListItemDrawerClose = () => {
    setSelectedProductId('')
    setIsSelectedListItemDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedProductId(id)
    setIsSelectedListItemDrawerOpen(true)
  }

  const handleProductDelete = () => {
    if (!selectedProductId) return
    deleteProduct(selectedProductId)
      .then(unwrapResult)
      .then(() => {
        setSelectedProductId('')
        setIsSelectedListItemDrawerOpen(false)
        fetchProductsWithParams()
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the product.`)
      })
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Products`,
      link: baseUrl
    }
  ]

  const isLoading = loading === 'pending' || isCustomerGroupParameterGroupLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Products`}</PageTitle>
      </Box>
      <ListActions>
        <CustomButton
          onClick={() => navigate(`${baseUrl}/create/`)}
          size="small"
          startIcon={<AddIcon />}
        >
          {t`Create new product`}
        </CustomButton>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
        />
      </ListActions>
      <TabContext value={tabFromParams}>
        <WithLoader loading={isProductGroupParameterGroupLoading} rows={1} height={33}>
          <TabList onChange={handleTabChange} aria-label={t`Product group tab list`}>
            {productGroupOptions?.map(({ label, value }) => (
              <Tab key={value} label={label} value={value} />
            ))}
          </TabList>
        </WithLoader>
        <Box pt={1}>
          {productGroupOptions?.map(({ value }) => (
            <TabPanel sx={{ p: 0 }} key={value} value={value}>
              <ProductList
                rows={products}
                isLoading={isLoading}
                params={searchQueryParams}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={total_results}
                page={
                  searchQueryParams.start_index
                    ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                customerGroupsParameter={customerGroupParameterGroup}
                allowSelect={false}
                selectedProductId={selectedProductId}
                onRowClick={handleRowClick}
              />
            </TabPanel>
          ))}
        </Box>
        <UnsavedChangesProvider>
          <ProductDrawer
            open={isSelectedListItemDrawerOpen}
            handleClose={handleSelectedListItemDrawerClose}
            handleProductDelete={handleProductDelete}
          />
        </UnsavedChangesProvider>
      </TabContext>
    </Box>
  )
}

export default ProductListPage
